import classNames from "classnames";
import { startCase, uniqueId } from "lodash";
import { FC, useContext, useMemo, useRef } from "react";
import { ExportGapFilterType } from "@app/exports/enumerations";
import { RegionSetFilter } from "@app/exports/exportModels";
import { NetworkAssessmentAnalysisType, RegionSetStatusType } from "@app/projects/enumerators";
import SelectStatusFilter from "./SelectStatusFilter";
import { UserContext } from "@app/user/UserContext";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import Roles from "@app/user/roles";

interface SelectRegionSetFilterProps {
  disabled: boolean;
  onNetworkAssessmentAnalysisTypeChange: (networkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => void;
  onRegionSetFilterTypeChange: (filter: ExportGapFilterType) => void;
  onRegionSetIncludeChange: (selected: boolean, regionSetStatus: RegionSetStatusType) => void;
  pluralDisplay: string;
  regionSetFilter: RegionSetFilter;
  singularDisplay: string;
  testIdSuffix: string;
}

const SelectRegionSetFilter: FC<SelectRegionSetFilterProps> = (props: SelectRegionSetFilterProps) => {
  const userContext = useContext(UserContext);
  const testIdSuffix = useRef("select-region-set-filter").current;

  const regionSetFilters = useMemo(() => {
    const filters = [
      { key: ExportGapFilterType.All, value: `All ${startCase(props.pluralDisplay)}` },
      { key: ExportGapFilterType.Gaps, value: `${startCase(props.pluralDisplay)} with Gaps` },
      { key: ExportGapFilterType.FillableBySingleProvider, value: `${startCase(props.pluralDisplay)} with Gaps Fillable by Single Provider` },
      { key: ExportGapFilterType.Exceptions, value: `${startCase(props.pluralDisplay)} with Exceptions` },
    ];
    if (userContext.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER)) {
      filters.push(
        { key: ExportGapFilterType.FillableGaps, value: `${startCase(props.pluralDisplay)} with Fillable Gaps` }
      )
    }
    return filters;
  }, [userContext.roles]);

  const isOpportunityAnalysisType =
    [NetworkAssessmentAnalysisType.Opportunity,
    NetworkAssessmentAnalysisType.ProjectedOpportunity,
    NetworkAssessmentAnalysisType.TermedOpportunity,
    NetworkAssessmentAnalysisType.ProjectedTermedOpportunity]
      .includes(props.regionSetFilter.networkAssessmentAnalysisType);
  const hasIncludes = props.regionSetFilter.includeActive || props.regionSetFilter.includePending || props.regionSetFilter.includeApproved;
  const includeActiveId = useRef(uniqueId("include-active-regionsets")).current;
  const includePendingId = useRef(uniqueId("include-pending-regionsets")).current;
  const includeApprovedId = useRef(uniqueId("include-approved-regionsets")).current;

  return (
    <>
      <p className="lead mb0 demibold">{startCase(props.singularDisplay)} Filter{props.disabled && <span>: Selected</span>}</p>
      <p>Please select the {props.pluralDisplay} to include:</p>

      <div className={classNames("form-group row", { "text-danger": !hasIncludes })}>
        <label className="col-12 col-form-label ptb0 text-start">
          Include {!hasIncludes && <i className="fa fa-exclamation-triangle mt5" style={{ position: "absolute" }} />}
        </label>
        <SelectStatusFilter
          disabled={true}
          onNetworkAssessmentAnalysisTypeChange={props.onNetworkAssessmentAnalysisTypeChange}
          regionSetFilter={props.regionSetFilter}
          testIdSuffix={testIdSuffix}
        />
        <div className="col-12 mt10">
          <QESCheckbox
            className="mt-0"
            label={`Active ${startCase(props.pluralDisplay)}`}
            id={includeActiveId}
            disabled={props.disabled || isOpportunityAnalysisType}
            value="active"
            checked={props.regionSetFilter.includeActive && !isOpportunityAnalysisType}
            onChange={(e) => props.onRegionSetIncludeChange(e.target.checked, RegionSetStatusType.Active)}
            testId="select-region-set-filter__active"
          />
          <QESCheckbox
            className="mt-0"
            label={`Pending ${startCase(props.pluralDisplay)}`}
            id={includePendingId}
            disabled={props.disabled || isOpportunityAnalysisType}
            value="pending"
            checked={props.regionSetFilter.includePending && !isOpportunityAnalysisType}
            onChange={(e) => props.onRegionSetIncludeChange(e.target.checked, RegionSetStatusType.Pending)}
            testId="select-region-set-filter__pending"
          />
          <QESCheckbox
            className="mt-0"
            label={`Approved ${startCase(props.pluralDisplay)}`}
            id={includeApprovedId}
            disabled={props.disabled || isOpportunityAnalysisType}
            value="approved"
            checked={props.regionSetFilter.includeApproved && !isOpportunityAnalysisType}
            onChange={(e) => props.onRegionSetIncludeChange(e.target.checked, RegionSetStatusType.Approved)}
            testId="select-region-set-filter__approved"
          />
        </div>
      </div>
      <div className="form-group row qes-radio-group__container">
        <p className="col-12 col-form-label ptb0 text-start mb-1">Filter</p>
        {regionSetFilters.map(({ key, value }) => {
          return (
            <div key={key} className="col-12 mt0">
              <QESRadioButton
                label={value}
                className="mt-0"
                disabled={props.disabled}
                value={key}
                checked={props.regionSetFilter.filterType === key}
                onChange={() => props.onRegionSetFilterTypeChange(key)}
                testId={`select-region-filter__${key}--${props.testIdSuffix}`}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}

export default SelectRegionSetFilter;
